import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { Button, Space, Popover, message, Tooltip } from 'antd';
import { CheckOutlined, DownOutlined, CaretRightOutlined } from "@ant-design/icons";
import { Collapse, Modal, Typography} from "antd";
import { fetchHistory } from "src/actions/historyTrial";
import { Loading, FinalizateBar, FinalizateButton } from 'src/DigitalProtocolModule/components';
import {
  ConfirmModal,
  MultiSwitch,
} from 'src/DigitalProtocolModule/components';
import SearchableCustomSelect from 'src/DigitalProtocolModule/components/SearchableCustomSelect';
import PanelHeader from "src/DigitalProtocolModule/components/EndpointPanel/PanelHeader";
import {
  getDpEndpointList,
  insertEndpointDetail,
  updateEndpointDetail,
  deleteEndpointDetail,
  getEndpointLibraryList
} from "src/DigitalProtocolModule/utils/ajax-proxy";
import EndpointDetailsModal from 'src/DigitalProtocolModule/components/AddEndpointsModal';
import styles from './index.module.scss';
import './index.scss';

const { Panel } = Collapse;
const { Text } = Typography;

const panelContent = (record) => {
  return (
    <div className={styles.endpointPanelBody}>

      <div>
        <span className={styles.key}>Analysis Set</span><br/>
        <span className={styles.value}>
          {(() => {            
            const analysisSetName = record["analysis_set_name"];
            
            if (Array.isArray(analysisSetName) && analysisSetName.length === 1 && analysisSetName[0] === null) {
              // If analysisSetName is null, undefined, or any falsy value
              return '-';
            }

            if (typeof analysisSetName === 'string') {
              // If it's a non-empty string, return it; otherwise, return '-'
              return analysisSetName.trim() || '-';
            }

            if (Array.isArray(analysisSetName)) {
              // If it's an array, return joined values or '-' if empty
              return analysisSetName.length > 0
                ? analysisSetName.join(', ')
                : '-';
            }

            // For any other type, return '-'
            return '-';
          })()}
        </span>
      </div>

      <div>
        <span className={styles.key}>Confidence Level</span><br/>
        <span className={styles.value}> 
        {record["confidence_interval"]&&record["confidence_interval"].length > 30?(
          <Text             
            ellipsis={{tooltip:record["confidence_interval"]}}            
          >
            {record["confidence_interval"] || "-"}
          </Text>
        ):(
          <span>{record["confidence_interval"] || "-"}</span>
        )}
        </span>
      </div>

      <div>
        <span className={styles.key}>N Sides</span><br/>
        <span className={styles.value}> 
        {record["n_sides"]&&record["n_sides"].length > 30?(
          <Text             
            ellipsis={{tooltip:record["n_sides"]}}            
          >
            {record["n_sides"] || "-"}
          </Text>
        ):(
          <span>{record["n_sides"] || "-"}</span>
        )}
        </span>
      </div>

      <div>
        <span className={styles.key}>Significance Level(Alpha)</span><br/>
        <span className={styles.value}> 
        {record["significance_level"]&&record["significance_level"].length > 30?(
          <Text                     
            ellipsis={{tooltip:record["significance_level"]}}            
          >
            {record["significance_level"] || "-"}
          </Text>
        ):(
          <span>{record["significance_level"] || "-"}</span>
        )}
        </span>     
      </div>
    </div>
  );
};


export default function Endpoints() {
  
  const trial_uuid = localStorage.getItem("trial-id");  

  const finalizeStatus = useSelector(fetchHistory).data?.dpFinalizeStatus;

  const isLocked = useMemo(
    () => Boolean(finalizeStatus?.endpoint_finalize_flag),
    [finalizeStatus]
  );

  const [data, setData] = useState({
    primary_endpoint_list: [],
    secondary_endpoint_list: [],
    exploratory_endpoint_list: [],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isShowFinalize, setIsShowFinalize] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [endptModalVisible, setEndptModalVisible] = useState(false);
  const [currentEndpointType, setCurrentEndpointType] = useState(1);
  const [currentEndpointData, setCurrentEndpointData] = useState(null);
  const [unit, setUnit] = useState('Day');
  const [nameReadOnly, setNameReadOnly] = useState(false);
  const [options, setOptions] = useState([]);
  const [endpointLibList, setEndpointLibList] = useState([]);
  const [title, setTitle] = useState('');
  const [isShowDelConfim, setIsShowDelConfim] = useState({ isOpen: false, endpointName: '', record: null });

  // State to track the collapse state for each section
  const [primaryCollapsed, setPrimaryCollapsed] = useState(true);
  const [secondaryCollapsed, setSecondaryCollapsed] = useState(true);
  const [exploratoryCollapsed, setExploratoryCollapsed] = useState(true);

  const togglePrimary = () => setPrimaryCollapsed(!primaryCollapsed);
  const toggleSecondary = () => setSecondaryCollapsed(!secondaryCollapsed);
  const toggleExploratory = () => setExploratoryCollapsed(!exploratoryCollapsed);

  const headerRef = useRef(null);

  const fetchEndpointLibList = async () => {
    setIsLoading(true);
    try {    
      const res = await getEndpointLibraryList(); 
      if (res) {
        setEndpointLibList(res);
        setIsLoading(false);	
      }     
    } catch (error) {
      setIsLoading(false);
      message.error('Failed to fetch the endpoints list')
    }
  }

  const fetchData = async () => {
    setIsLoading(true);
    try {    
      const res = await getDpEndpointList(trial_uuid);   
      if (res) {
        setData(res);
        setIsLoading(false);	
      } 	
    } catch (error) {
      setIsLoading(false);
      message.error('Failed to fetch the endpoints data')
    }
  }

  useEffect(()=>{
    fetchData();
    fetchEndpointLibList();
  },[])

  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        const headerOffset = headerRef.current.offsetTop;
        if (window.pageYOffset > headerOffset) {
          headerRef.current.classList.add(styles.fixedHeader);
        } else {
          headerRef.current.classList.remove(styles.fixedHeader);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const showModal =(endpointType) =>{
    const numericType = parseInt(endpointType, 10); // Convert to number
    setCurrentEndpointType(numericType);
    setTitle(getTitle(numericType));
    setVisible(true);
  }

  const callCancel = () => {
    setVisible(false);
  }

  const handleAddNewClick = () => {    
    // Reset current endpoint data
    setCurrentEndpointData({
      endpointName: '',
      description: '',
      statisticalMethod: '',
      parameters: '',
      confidenceInterval: '',
      significanceLevel: '',
      nSides: '',
      analysisSet: [],
      days: [],
      weeks: []
    });
  
    // Set name read-only to false to allow input
    setNameReadOnly(false);
  
    // Set the mode to adding instead of editing
    setIsEdit(false);
  
    // Open the endpoint modal
    setEndptModalVisible(true);
      
    // Close the search modal
    callCancel();
  };
  

  const onEditEndpoint = (record, endptTypeID) => {    
    const endpointTypeId = endptTypeID;
    setCurrentEndpointType(endpointTypeId);
    setCurrentEndpointData(record);
    setIsEdit(true);
    setNameReadOnly(true);
    setEndptModalVisible(true);    
  };

  const onDeleteEndpointConfirm = (record) => {    
    setIsShowDelConfim({ isOpen: true, endpointName: record?.endpoint_name, record });
  }

  const onDeleteEndpoint = async (record) => {
    try {
      const payload = {
        "dprot_endpoint_id": record.dprot_endpoint_id
      };
      
      await deleteEndpointDetail(payload); // Call API to delete endpoint
      message.success("Deleted successfully");
      fetchData(); // Refresh data
    } catch (error) {
      message.error("Failed to delete endpoint");
    }
  };

  useEffect(() => {
    // Update options whenever the current endpoint type changes
    if (endpointLibList) {      
      const options = convertEndpointList(endpointLibList);
      setOptions(options);
    }
  }, [endpointLibList]);  

  function convertEndpointList(endpointData) {
    let list;
    try {
        list = endpointData.endpoint_list;
        
        // Check if the list is defined and is an array
        if (!Array.isArray(list)) {
            throw new Error('List is not an array');
        }

        return list.map(endpoint => ({
            label: endpoint.endpoint_std_term || "Unnamed",
            value: String(endpoint.endpoint_id)
        }));
    } catch (error) {
        console.error("An error occurred:", error.message);
        return [];
    }
  }

  const onSaveEndpoint = async (endpointData, isNameReadOnly) => {
    setIsLoading(true);    
    // Destructure the endpointData directly without defaults
    const {
      endpointName,
      description,
      statisticalMethod,
      analysisSet,
      confidenceInterval,
      significanceLevel,
      parameters,
      nSides,
      days,
      weeks,
      dprot_endpoint_id,
      user_defined_endpoint
    } = endpointData;
  
    // Create a common payload for both insert and update
    const commonPayload = {
      endpoint_id: null,
      endpoint_name: endpointName,
      endpoint_type_id: currentEndpointType,
      endpoint_description: description,
      test_name: statisticalMethod,
      analysis_set_map_id: analysisSet,
      analysis_set_name: analysisSet,
      confidence_interval: confidenceInterval,
      significance_level: significanceLevel,
      parameters: parameters,
      n_sides: nSides,
      days: days,
      weeks: weeks
    };
  
    // Payload for insert operation
    const payload = {
      ...commonPayload,
      trial_uuid: trial_uuid,
      user_defined_endpoint: !isNameReadOnly,
    };
  
    // Payload for update operation
    const editPayload = {
      ...commonPayload,
      dprot_endpoint_id: dprot_endpoint_id,
      user_defined_endpoint: user_defined_endpoint,
    };
    // Set the endpoint ID for the update operation
    try {
      // Determine which API call to make based on isEdit flag
      const response = isEdit
        ? await updateEndpointDetail(editPayload)
        : await insertEndpointDetail(payload);
  
      // Check response
      if (isEdit) {
        if(response.error){
          message.error("Failed to update");          
        }else{
          message.success("Updated successfully");
          fetchData(); // Refresh data
        }
      } else {
        if(response.error){
          message.error("Failed to save");          
        }else{
          message.success("Saved successfully");
          fetchData(); // Refresh data          
        }
      }
    } catch (error) {      
      message.error("Failed to save");
      setIsEdit(false);
    } finally {
      // Reset loading state and close modal
      setIsLoading(false);
      setIsEdit(false);
      setVisible(false);
    }
  };


  useEffect(() => {
    if (isEdit) {
      // Code to handle when isEdit becomes true
      setIsEdit(true);
    } else {
      // Code to handle when isEdit becomes false
      setIsEdit(false);
    }
  }, [isEdit]);
      
  // Function to determine the modal title based on endpointType
  const getTitle = (type) => {    
    switch (type) {
      case 1:
        return "Add Primary Endpoint";
      case 2:
        return "Add Secondary Endpoint";
      case 4:
        return "Add Exploratory Endpoint";
      default:
        return "Add Endpoint";
    }
  };
  //Should enable below code once API is implemented

  // const fetchUnits = async () => {
  //   try {
  //     // Fetch the unit from the server and set it
  //     // const response = await getDpEndpointUnit(trial_uuid);
  //     const response = { unit: 'Day' };
  //     if (response?.unit) {
  //       setUnit(response.unit);
  //     }
  //   } catch (error) {
  //     message.error("Failed to fetch unit");
  //   }
  // };

  const updateUnit = async (newUnit) => {
    try {
      // await updateDpEndpointUnit({ trial_uuid, unit: newUnit });
      setUnit(newUnit);
      fetchData();
      // message.success("Unit updated successfully");
    } catch (error) {
      // message.error("Failed to update unit");
    }
  };

  // useEffect(() => {
  //   fetchData();
  //   fetchUnits(); // Fetch initial unit value
  // }, []);
  
  return (
    <>
    	{isLocked && (
        <div className={styles.topBar}>
          <FinalizateBar category="endpoints" />
			  </div>
      )}
      <div className={styles.header} >
        <span className={styles.title}>Endpoints</span>
        <div className={styles.btns}>
          {!isLocked && <FinalizateButton category="endpoints" />}
        </div>
      </div>
      <div className={styles.bar}>
        <div>
          <label>Timeframe Unit</label>
          <MultiSwitch
            options={['Day', 'Week']}
            value={unit}
            onChange={(val) => {
              if (unit !== val) {
                setUnit(val)
                updateUnit(val)
              }
            }}
            disabled={isLocked}
          />
        </div>
      </div>
      <div className={styles.endpointListContainer} ref={headerRef}>
        <div className={styles.endpointList}>
          <div className={styles.listColumns}>
            <div className={styles.colItem}>Endpoint</div>
            <div className={styles.colItem}>Timeframe({unit})</div>
            <div className={styles.colItem}>Statistical Test</div>
            <div className={styles.colItem}>Parameters</div>          
            <div className={styles.colItem}></div>
          </div>
        </div>
      </div>
      <Loading isLoading={isLoading}>		
        {/* Primary Endpoints */}      
        <div className={`${styles.endpointContainer} endpointCollapseContainer`}>
          <div className={styles.endpointHeader} onClick={togglePrimary}>
            <CaretRightOutlined
              className={styles.caretRightIcon} // Add a custom class if needed
              rotate={primaryCollapsed ? 90 : 0} // Rotate it based on the collapsed state
            />  
            <span className={styles.endpointTitle}> Primary Endpoint ({data.primary_endpoint_list.length}) </span>
          </div>
          {primaryCollapsed && (
            <Collapse
              className= "expand-icon"
              expandIconPosition="right"
              expandIcon={({ isActive }) => <DownOutlined className={styles.downOutlinedIcon} rotate={isActive ? 180 : 0} />}   
            >
              {Array.isArray(data.primary_endpoint_list) && data.primary_endpoint_list.length > 0 &&
                data.primary_endpoint_list.map((d: any, idx: number) => (
                  <Panel
                    header={
                      <PanelHeader
                        record={d}
                        idx={idx}
                        unit={unit}
                        onEdit={onEditEndpoint}
                        onDelete={onDeleteEndpointConfirm}
                        endptTypeID={1}
                        datalength={data.primary_endpoint_list.length}
                        isLocked={isLocked}
                      />
                    }
                    key={idx}
                  >
                    {panelContent(d)}
                  </Panel>
                ))}
            </Collapse>
          )}  
          {!isLocked &&
            <div className={styles.endPointFooter} onClick={() => showModal('1')}>
              <Button
                className={styles.linkBtn}
                key="add_primary_endpoint"
                type="text"
                size="small"                     
              >
                ADD PRIMARY ENDPOINT
              </Button>
            </div>
          }
        </div>   

        {/* Secondary Endpoints */}
        <div className={`${styles.endpointContainer} endpointCollapseContainer`}>
          <div className={styles.endpointHeader} onClick={toggleSecondary}>
            <CaretRightOutlined
              className={styles.caretRightIcon} // Add a custom class if needed
              rotate={secondaryCollapsed ? 90 : 0} // Rotate it based on the collapsed state
            />          
            <span className={styles.endpointTitle}>
              Secondary Endpoint ({data.secondary_endpoint_list.length})
            </span>
          </div>
          {secondaryCollapsed && (
            <Collapse
              className="expand-icon"
              expandIconPosition="right"
              expandIcon={({ isActive }) => (
                <DownOutlined
                  className={styles.downOutlinedIcon} 
                  rotate={isActive ? 180 : 0}
                />
              )}
            >
              {Array.isArray(data.secondary_endpoint_list) && data.secondary_endpoint_list.length > 0 &&
                data.secondary_endpoint_list.map((d: any, idx: number) => (
                  <Panel
                    header={
                      <PanelHeader
                        record={d}
                        idx={idx}
                        unit={unit}
                        onEdit={onEditEndpoint}
                        onDelete={onDeleteEndpointConfirm}
                        endptTypeID={2}
                        datalength={data.secondary_endpoint_list.length}
                        isLocked={isLocked}
                      />
                    }
                    key={idx}
                  >
                    {panelContent(d)}
                  </Panel>
                ))}
            </Collapse>
          )}  
          {!isLocked &&
            <div className={styles.endPointFooter} onClick={() => showModal('2')}>
              <Button
                className={styles.linkBtn}
                key="add_secondary_endpoint"
                type="text"
                size="small"              
              >
                ADD SECONDARY ENDPOINT
              </Button>
            </div>
          }
        </div>

        {/* Exploratory Endpoints */}
        <div className={`${styles.endpointContainer} endpointCollapseContainer`}>
          <div className={styles.endpointHeader} onClick={toggleExploratory}>
            <CaretRightOutlined
              className={styles.caretRightIcon} // Add a custom class if needed
              rotate={exploratoryCollapsed ? 90 : 0} // Rotate it based on the collapsed state
            />  
            <span className={styles.endpointTitle}>
              Exploratory Endpoint ({data.exploratory_endpoint_list.length})
            </span>
          </div>
          {exploratoryCollapsed && (
            <Collapse
              className="expand-icon"
              expandIconPosition="right"
              expandIcon={({ isActive }) => (
                <DownOutlined
                  className={styles.downOutlinedIcon} 
                  rotate={isActive ? 180 : 0}
                />
              )}
            >
              {Array.isArray(data.exploratory_endpoint_list) && data.exploratory_endpoint_list.length > 0 &&
                data.exploratory_endpoint_list.map((d: any, idx: number) => (
                  <Panel
                    header={
                      <PanelHeader
                        record={d}
                        idx={idx}
                        unit={unit}
                        onEdit={onEditEndpoint}
                        onDelete={onDeleteEndpointConfirm}
                        endptTypeID={4}
                        datalength={data.exploratory_endpoint_list.length}
                        isLocked={isLocked}
                      />
                    }
                    key={idx}
                  >              
                    {panelContent(d)}
                  </Panel>
                ))}
            </Collapse>
          )}  
          {!isLocked &&
            <div className={styles.endPointFooter} onClick={() => showModal('4')}>
              <Button
                className={styles.linkBtn}
                key="add_exploratory_endpoint"
                type="text"
                size="small"              
              >
                ADD EXPLORATORY ENDPOINT
              </Button>
            </div>
          }
        </div>
      </Loading>
      <Modal
        title={title}
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <>
          <Button key="cancel" onClick={callCancel}>
            Cancel
          </Button>
          <Button
            className={styles.addNewBtn}
            size="small"
            type="link"
            onClick={handleAddNewClick}
          >
            Add New
          </Button>
          </>
        ]}
      >
        <div className={styles.modalContent}>
          <SearchableCustomSelect options={options} onClickSubmit={onSaveEndpoint} unit={unit} callCancel={callCancel} />
        </div>
      </Modal>  
       
      <EndpointDetailsModal
        visible={endptModalVisible}
        onCancel={() => setEndptModalVisible(false)}
        onSave={onSaveEndpoint}
        initialData={currentEndpointData} // Pass the current endpoint data for editing        
        isNameReadOnly={nameReadOnly} // Set to true if endpoint name is read-only
        isEditData={isEdit} // Set to true if editing existing endpoint data
        unit={unit}        
      />

      <ConfirmModal      
        visible={isShowDelConfim.isOpen}
        title={`Delete Endpoint: ${isShowDelConfim.endpointName}`}
        content={
          'This action will delete the selected endpoint. Are you sure you want to delete it?'
        }
        handleCancel={() => setIsShowDelConfim({ isOpen: false, endpointName: '', record: null })}
        handleConfirm={() => {
          onDeleteEndpoint(isShowDelConfim.record);
          setIsShowDelConfim({ isOpen: false, endpointName: '', record: null });
        }}
      />
    </>
  )
}
